import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Card, Col, Container, Row } from "react-bootstrap";

import axiosRestaurantInstance from "../restaurant/AxiosRestaurantInstance";
import MayaBrandMark from "../components/MayaBrandMark";
import { ORGANIZATION_API_PATHS } from "../constants/apiPaths";
import { storeLoginTokens } from "../utils/sessionManager";
import { requestPermissionAndSendToken } from "../firebase-config";
import {
  applyPartnerSession,
  clearPendingPartnerLogin,
  formatPartnerContextType,
  getPendingPartnerLogin,
} from "./partnerSession";
import { PARTNER_LOGIN_PATH } from "./PartnerLogin";
import "./PartnerLogin.css";

const PartnerChooseBusiness = ({ onLogin }) => {
  const navigate = useNavigate();
  const [pendingLogin, setPendingLogin] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const stored = getPendingPartnerLogin();
    if (!stored?.contexts?.length || !stored.identifier || !stored.password) {
      navigate(PARTNER_LOGIN_PATH, { replace: true });
      return;
    }
    setPendingLogin(stored);
  }, [navigate]);

  const finishLogin = (response, sessionUser) => {
    const token =
      response.headers?.["x-auth-token"] ||
      response.headers?.["X-Auth-Token"] ||
      response.headers?.get?.("X-Auth-Token");

    if (!token) {
      setError("Login failed: auth token missing.");
      return;
    }

    storeLoginTokens(response);
    applyPartnerSession(sessionUser);
    clearPendingPartnerLogin();

    if (typeof onLogin === "function") onLogin();
    requestPermissionAndSendToken().catch(() => {});
    navigate(sessionUser.homePath || PARTNER_LOGIN_PATH);
  };

  const handleSelectContext = async (contextId) => {
    if (!pendingLogin) return;

    setLoading(true);
    setError("");
    try {
      const response = await axiosRestaurantInstance.post(
        ORGANIZATION_API_PATHS.partnerLogin,
        {
          identifier: pendingLogin.identifier,
          email: pendingLogin.identifier,
          userName: pendingLogin.identifier,
          mobile: pendingLogin.identifier,
          password: pendingLogin.password,
          contextId,
        },
        { skipAuthRefresh: true }
      );
      const data = response.data || {};
      if (!data.sessionUser) {
        setError(data.message || "Unable to open selected business.");
        return;
      }
      finishLogin(response, data.sessionUser);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Unable to open selected business."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBackToSignIn = () => {
    clearPendingPartnerLogin();
    navigate(PARTNER_LOGIN_PATH);
  };

  if (!pendingLogin) {
    return null;
  }

  return (
    <div className="partner-choose-page">
      <Container fluid className="partner-choose-page__shell px-3 py-3">
        <Row className="partner-choose-page__row g-0 justify-content-center align-items-center">
          <Col xs={12} sm={10} md={7} lg={5} xl={4} className="partner-choose-page__col">
            <Card className="partner-choose-card border-0 shadow-sm mx-auto">
              <Card.Body className="partner-choose-card__body">
                <div className="partner-choose-brand text-center">
                  <MayaBrandMark variant="login" />
                </div>

                <div className="partner-choose-header text-center">
                  <h1 className="partner-choose-title">Choose business</h1>
                  <p className="partner-choose-subtitle">
                    This account has access to more than one business. Select where to
                    continue.
                  </p>
                </div>

                {error ? (
                  <Alert variant="danger" className="mb-3 text-start">
                    {error}
                  </Alert>
                ) : null}

                <div className="partner-choose-list" role="list">
                  {pendingLogin.contexts.map((ctx) => (
                    <button
                      key={ctx.id}
                      type="button"
                      role="listitem"
                      disabled={loading}
                      className="partner-choose-list__item"
                      onClick={() => handleSelectContext(ctx.id)}
                    >
                      <strong>{ctx.businessName || ctx.label}</strong>
                      <span>
                        {formatPartnerContextType(ctx.type)} · {ctx.role}
                      </span>
                    </button>
                  ))}
                </div>

                <div className="partner-choose-footer text-center">
                  <Button
                    variant="link"
                    className="partner-choose-back"
                    disabled={loading}
                    onClick={handleBackToSignIn}
                  >
                    Back to sign in
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PartnerChooseBusiness;
